import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listActivities } from "../api/endpoints";
import type { ActivitySummary } from "../api/types";
import { Badge, Button, Card, EmptyState, ErrorMessage, Input, PageHeading, Spinner } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDistance(distanceMeters: number | null): string {
  if (distanceMeters == null) return "—";
  return `${(distanceMeters / 1000).toFixed(1)} km`;
}

function formatDuration(durationMinutes: number | null): string {
  if (durationMinutes == null) return "—";
  return `${durationMinutes} min`;
}

function ActivityCard({ activity }: { activity: ActivitySummary }) {
  return (
    <Card className="p-4" data-testid="activities-list-card">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{activity.activityName}</h2>
            <Badge>{activity.activityType}</Badge>
            {activity.perceivedEffort != null && <Badge>RPE {activity.perceivedEffort}</Badge>}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(activity.activityDate)}</p>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
            <span>{formatDistance(activity.distanceMeters)}</span>
            <span>{formatDuration(activity.durationMinutes)}</span>
            <span>{activity.averageHeartrate != null ? `${activity.averageHeartrate} bpm avg` : "No HR"}</span>
            <span>{activity.elevationGainM != null ? `${activity.elevationGainM} m climb` : "Flat"}</span>
          </div>
        </div>
        <Link to={`/activities/${activity.id}`} className="text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400" data-testid="activities-card-detail-link">
          View detail
        </Link>
      </div>
    </Card>
  );
}

export default function ActivitiesPage() {
  const [page, setPage] = useState(1);
  const [draftType, setDraftType] = useState("");
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [filters, setFilters] = useState({ activityType: "", from: "", to: "" });

  const { data, error, loading, reload } = useAsync(
    () =>
      listActivities({
        page,
        activityType: filters.activityType || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
      }),
    [page, filters.activityType, filters.from, filters.to],
  );

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.total / data.limit));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="activities-page">
      <PageHeading title="Activity timeline" subtitle="Browse imported runs, rides, hikes and more." />

      <Card className="p-4" data-testid="activities-filter-panel">
        <div className="grid gap-4 md:grid-cols-4">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>Activity type</span>
            <Input value={draftType} onChange={(event) => setDraftType(event.target.value)} placeholder="run, ride, hike" data-testid="activities-type-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>From</span>
            <Input type="date" value={draftFrom} onChange={(event) => setDraftFrom(event.target.value)} data-testid="activities-from-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>To</span>
            <Input type="date" value={draftTo} onChange={(event) => setDraftTo(event.target.value)} data-testid="activities-to-input" />
          </label>
          <div className="flex items-end gap-2">
            <Button data-testid="activities-apply-button" onClick={() => { setFilters({ activityType: draftType.trim(), from: draftFrom, to: draftTo }); setPage(1); }}>
              Apply filters
            </Button>
            <Button variant="secondary" data-testid="activities-clear-button" onClick={() => { setDraftType(""); setDraftFrom(""); setDraftTo(""); setFilters({ activityType: "", from: "", to: "" }); setPage(1); }}>
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {loading && <Spinner />}
      {error && !loading && <ErrorMessage error={error} onRetry={reload} />}
      {!loading && !error && data && data.activities.length === 0 && (
        <EmptyState title="No activities found" description="Adjust the filters or import another activity to populate your timeline." testId="activities-empty-state" />
      )}
      {!loading && !error && data && data.activities.length > 0 && (
        <div className="space-y-3" data-testid="activities-list-panel">
          {data.activities.map((activity) => (
            <ActivityCard key={activity.id} activity={activity} />
          ))}
        </div>
      )}

      <div className="flex items-center justify-center gap-4" data-testid="activities-pagination-panel">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))} data-testid="activities-previous-button">Previous</Button>
        <span className="text-sm text-gray-500 dark:text-gray-400" data-testid="activities-page-label">Page {page} of {totalPages}</span>
        <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))} data-testid="activities-next-button">Next</Button>
      </div>
    </div>
  );
}
